import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { EmailReceiver, EmailAttachments } from '../types/invoice.types';
import { showWarning } from '@/utils/sweetAlert';

interface ResendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (attachments: EmailAttachments, receivers: EmailReceiver[]) => void;
  isSubmitting?: boolean;
}

export function ResendEmailModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: ResendEmailModalProps) {
  const [attachments, setAttachments] = useState<EmailAttachments>({
    dResponseAuthXML: false,
    dResponseXML: false,
    dCFe: true,
    dCAFE: true,
  });

  const [receivers, setReceivers] = useState<EmailReceiver[]>([
    { Email: '', Name: '' },
  ]);

  const handleAttachmentChange = (key: keyof EmailAttachments) => {
    setAttachments((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleReceiverChange = (index: number, field: keyof EmailReceiver, value: string) => {
    setReceivers((prev) => {
      const newReceivers = [...prev];
      newReceivers[index] = {
        ...newReceivers[index],
        [field]: value,
      };
      return newReceivers;
    });
  };

  const addReceiver = () => {
    setReceivers((prev) => [...prev, { Email: '', Name: '' }]);
  };

  const removeReceiver = (index: number) => {
    if (receivers.length > 1) {
      setReceivers((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate at least one attachment is selected
    const hasAttachment = Object.values(attachments).some((v) => v);
    if (!hasAttachment) {
      showWarning('Adjuntos Requeridos', 'Debe seleccionar al menos un documento adjunto');
      return;
    }

    // Validate all receivers have email
    const validReceivers = receivers.filter((r) => r.Email.trim() !== '');
    if (validReceivers.length === 0) {
      showWarning('Destinatario Requerido', 'Debe agregar al menos un destinatario con email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validReceivers.filter((r) => !emailRegex.test(r.Email));
    if (invalidEmails.length > 0) {
      showWarning('Email Inválido', 'Por favor ingrese emails válidos');
      return;
    }

    // Set name same as email if empty
    const finalReceivers = validReceivers.map((r) => ({
      Email: r.Email,
      Name: r.Name.trim() || r.Email,
    }));

    onSubmit(attachments, finalReceivers);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Reenviar Factura por Email
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Attachments Section */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Documentos a Adjuntar
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={attachments.dCFe}
                          onChange={() => handleAttachmentChange('dCFe')}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">CFE (Comprobante Fiscal Electrónico)</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={attachments.dCAFE}
                          onChange={() => handleAttachmentChange('dCAFE')}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">CAFE (Código de Autorización)</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={attachments.dResponseXML}
                          onChange={() => handleAttachmentChange('dResponseXML')}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">Response XML</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={attachments.dResponseAuthXML}
                          onChange={() => handleAttachmentChange('dResponseAuthXML')}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">Response Auth XML</span>
                      </label>
                    </div>
                  </div>

                  {/* Receivers Section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Destinatarios
                      </label>
                      <button
                        type="button"
                        onClick={addReceiver}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100"
                        disabled={isSubmitting}
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Agregar
                      </button>
                    </div>

                    <div className="space-y-3">
                      {receivers.map((receiver, index) => (
                        <div key={index} className="flex flex-col sm:flex-row gap-2">
                          <div className="flex-1">
                            <input
                              type="email"
                              placeholder="Email"
                              value={receiver.Email}
                              onChange={(e) =>
                                handleReceiverChange(index, 'Email', e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              disabled={isSubmitting}
                              required
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Nombre (opcional)"
                              value={receiver.Name}
                              onChange={(e) =>
                                handleReceiverChange(index, 'Name', e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              disabled={isSubmitting}
                            />
                          </div>
                          {receivers.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeReceiver(index)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 hover:text-red-900"
                              disabled={isSubmitting}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3 mt-6 [&>*]:w-full sm:[&>*]:w-auto">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Email'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
